import { useState } from 'react';
import { Button, Space } from 'antd';
import {
  LockOutlined,
  UnlockOutlined,
  CheckCircleOutlined,
  MailOutlined,
  NotificationOutlined,
} from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';
import { useUserActions } from '@/hooks/iam/useUserActions';
import { SendPushNotificationModal } from './SendPushNotificationModal';

interface UserActionButtonsProps {
  userId: string | number;
  isActive?: boolean;
  isVerified?: boolean;
  /**
   * Render mode: 'all' shows all applicable buttons, 'compact' shows only primary action
   */
  mode?: 'all' | 'compact';
}

/**
 * Reusable user action buttons component
 * Can be used in both list and show views
 */
export const UserActionButtons = ({
  userId,
  isActive = true,
  isVerified = true,
  mode = 'all',
}: UserActionButtonsProps) => {
  const t = useTranslate();
  const actions = useUserActions({ userId });
  const [pushOpen, setPushOpen] = useState(false);

  if (mode === 'compact') {
    // In compact mode (e.g., for list view), show only the most important action
    if (isActive && actions.block.canExecute) {
      return (
        <Button
          size="small"
          danger
          icon={<LockOutlined />}
          onClick={actions.block.mutate}
          loading={actions.block.isLoading}
        >
          {t('iam.users.actions.block')}
        </Button>
      );
    }

    if (!isActive && actions.unblock.canExecute) {
      return (
        <Button
          size="small"
          type="primary"
          icon={<UnlockOutlined />}
          onClick={actions.unblock.mutate}
          loading={actions.unblock.isLoading}
        >
          {t('iam.users.actions.unblock')}
        </Button>
      );
    }

    return null;
  }

  // Full mode - show all applicable buttons
  return (
    <Space>
      {isActive && actions.block.canExecute && (
        <Button
          danger
          icon={<LockOutlined />}
          onClick={actions.block.mutate}
          loading={actions.block.isLoading}
        >
          {t('iam.users.actions.block')}
        </Button>
      )}

      {!isActive && actions.unblock.canExecute && (
        <Button
          type="primary"
          icon={<UnlockOutlined />}
          onClick={actions.unblock.mutate}
          loading={actions.unblock.isLoading}
        >
          {t('iam.users.actions.unblock')}
        </Button>
      )}

      {!isVerified && actions.verify.canExecute && (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={actions.verify.mutate}
          loading={actions.verify.isLoading}
        >
          {t('iam.users.actions.verify')}
        </Button>
      )}

      {actions.resetPassword.canExecute && (
        <Button
          icon={<MailOutlined />}
          onClick={actions.resetPassword.mutate}
          loading={actions.resetPassword.isLoading}
        >
          {t('iam.users.actions.reset_password')}
        </Button>
      )}

      <Button icon={<NotificationOutlined />} onClick={() => setPushOpen(true)}>
        {t('iam.users.actions.sendPush')}
      </Button>

      <SendPushNotificationModal
        open={pushOpen}
        onClose={() => setPushOpen(false)}
        userId={Number(userId)}
      />
    </Space>
  );
};
